import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AudioPlayer } from '../components/AudioPlayer';
import { BrandHeader } from '../components/BrandHeader';
import { getSurveyUrl } from '../config/externalLinks';
import type { Language } from '../content/content';
import { content, tourRoutes } from '../content/content';
import { setStoredLanguage } from '../utils/language';

type TourPageProps = {
  language: Language;
};

export function TourPage({ language }: TourPageProps) {
  const navigate = useNavigate();
  const copy = content[language];
  const nextLanguage: Language = language === 'es' ? 'en' : 'es';
  const [format, setFormat] = useState<'audio' | null>(null);
  const surveyUrl = getSurveyUrl(language);

  function changeLanguage() {
    if (window.confirm(copy.changeLanguageConfirm)) {
      setStoredLanguage(nextLanguage);
      navigate(tourRoutes[nextLanguage]);
    }
  }

  return (
    <main className="tourPage" lang={language}>
      <BrandHeader language={language} compact />
      <nav className="tourNav" aria-label={copy.tourTitle}>
        <Link to="/">{copy.backHome}</Link>
        <button type="button" onClick={changeLanguage}>
          {copy.changeLanguage}
        </button>
      </nav>
      <h1 className="screenReaderOnly">{copy.tourTitle}</h1>

      {format === null ? (
        <section className="formatChooser" aria-labelledby="format-question">
          <p className="eyebrow">{copy.tourTitle}</p>
          <h2 id="format-question">{copy.formatQuestion}</h2>
          <div className="formatOptions">
            <a className="formatOptionLink pdfOption" href={copy.pdfPath} target="_blank" rel="noreferrer">
              <strong>{copy.readPdf}</strong>
              <span>{copy.pdfDescription}</span>
            </a>
            <button type="button" onClick={() => setFormat('audio')}>
              <strong>{copy.listenAudio}</strong>
              <span>{copy.audioDescription}</span>
            </button>
            <a className="formatOptionLink" href={surveyUrl} target="_blank" rel="noreferrer">
              <strong>{copy.surveyCta}</strong>
              <span>{copy.surveyDescription}</span>
            </a>
          </div>
        </section>
      ) : (
        <>
          <div className="formatSwitch" aria-label={copy.chooseFormat}>
            <a href={copy.pdfPath} target="_blank" rel="noreferrer">
              {copy.readPdf}
            </a>
            <button
              type="button"
              className="active"
              aria-pressed="true"
              onClick={() => setFormat('audio')}
            >
              {copy.listenAudio}
            </button>
            <button type="button" onClick={() => setFormat(null)}>
              {copy.backToFormats}
            </button>
          </div>
          <AudioPlayer language={language} />
        </>
      )}
    </main>
  );
}
